/**
 * Maps a failed `POST /users` to a user-facing outcome.
 *
 * Handles both the codes requested from backend (docs/signup-v2/07-api-contract.md:
 * `EMAIL_IN_USE`, `STORE_NAME_TAKEN`, `PHONE_IN_USE`, `VALIDATION_ERROR` + `field`) and today's
 * responses (`200` + `error_code: 202`, or free-text `message`), by matching keywords.
 */

const EMAIL_WORDS = ['email', 'e-mail', 'correo', 'mail'];
const STORE_WORDS = ['store', 'tienda', 'url', 'domain', 'dominio', 'subdomain'];
const PHONE_WORDS = ['phone', 'telefono', 'teléfono', 'whatsapp', 'celular', 'mobile'];
const TAKEN_WORDS = [
    'exist',
    'in use',
    'en uso',
    'taken',
    'duplicate',
    'duplicad',
    'already',
    'registrad',
    'registered',
    'unique',
    'ya ',
];

const STORE_FIELDS = ['store', 'store_name', 'name', 'url', 'domain'];
const PHONE_FIELDS = ['phone', 'contact_phone_number', 'phone_number', 'mobile_phone_number'];

const includesAny = (text, words) => words.some((word) => text.includes(word));

const readMessage = (body) => {
    if (typeof body === 'string') return body;
    if (typeof body?.message === 'string') return body.message;
    if (typeof body?.error === 'string') return body.error;
    return '';
};

const readField = (body) => {
    const field = body?.field ?? body?.errors?.[0]?.field;
    return typeof field === 'string' ? field.toLowerCase() : '';
};

/**
 * @param {{ error?: any, data?: any }} failure thrown axios error, or the body of a 200 response
 *     that carries an `error_code`
 * @returns {{
 *     kind: 'network' | 'emailInUse' | 'field' | 'generic',
 *     field?: 'store' | 'phone',
 *     messageKey?: string,
 *     status: number | null,
 *     errorCode: string | number | null,
 *     message: string,
 *     code: string,
 * }}
 */
export const resolveSignupError = ({ error, data } = {}) => {
    if (error && !error.response) {
        return {
            kind: 'network',
            status: null,
            errorCode: null,
            message: error?.message || '',
            code: 'network',
        };
    }

    const status = error?.response?.status ?? null;
    const body = error?.response?.data ?? data ?? {};
    const errorCode = body?.error_code ?? body?.code ?? null;
    const message = readMessage(body);
    const field = readField(body);
    // Backend often repeats the HTTP status as `error_code` (e.g. 422 · 422): show it once.
    const code = [...new Set([status, errorCode].filter((part) => part != null && part !== ''))]
        .map(String)
        .join(' · ');
    const base = { status, errorCode, message, code: code || 'unknown' };

    const text = `${errorCode ?? ''} ${message}`.toLowerCase();
    const isTaken = status === 409 || includesAny(text, TAKEN_WORDS);

    if (
        errorCode === 202 ||
        errorCode === 'EMAIL_IN_USE' ||
        (field === 'email' && isTaken) ||
        (isTaken && includesAny(text, EMAIL_WORDS))
    ) {
        return { ...base, kind: 'emailInUse' };
    }

    if (
        errorCode === 'STORE_NAME_TAKEN' ||
        (STORE_FIELDS.includes(field) && isTaken) ||
        (isTaken && includesAny(text, STORE_WORDS))
    ) {
        return { ...base, kind: 'field', field: 'store', messageKey: 'SignUpV2.errors.storeTaken' };
    }

    if (
        errorCode === 'PHONE_IN_USE' ||
        (PHONE_FIELDS.includes(field) && isTaken) ||
        (isTaken && includesAny(text, PHONE_WORDS))
    ) {
        return { ...base, kind: 'field', field: 'phone', messageKey: 'SignUpV2.errors.phoneTaken' };
    }

    if (STORE_FIELDS.includes(field)) {
        return { ...base, kind: 'field', field: 'store', messageKey: 'SignUpV2.errors.storeInvalid' };
    }

    if (PHONE_FIELDS.includes(field)) {
        return { ...base, kind: 'field', field: 'phone', messageKey: 'SignUpV2.errors.phoneInvalid' };
    }

    return { ...base, kind: 'generic' };
};
